export const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1)

/**
 * The `Intl` locale for a UI language, always in the US region: a Spanish
 * reader here is still reading US dollars. Numeric dates still turn day-first
 * under `es-US`, so pair it with the `spelled` variant, or keep `formatUs*`
 * where a numeric date must read the same in both languages.
 */
export const toIntlLocale = (language: string | undefined): 'en-US' | 'es-US' =>
  language?.toLowerCase().startsWith('es') ? 'es-US' : 'en-US'

const US_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  month: '2-digit',
  day: '2-digit',
  year: 'numeric',
}

const US_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: '2-digit',
}

const US_DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  ...US_DATE_OPTIONS,
  ...US_TIME_OPTIONS,
}

// Module-scope formatters: Intl.DateTimeFormat is expensive to instantiate,
// so we build them once and reuse them across every call site.
const US_DATE_FORMAT = new Intl.DateTimeFormat('en-US', US_DATE_OPTIONS)
const US_TIME_FORMAT = new Intl.DateTimeFormat('en-US', US_TIME_OPTIONS)
const US_DATE_TIME_FORMAT = new Intl.DateTimeFormat('en-US', US_DATE_TIME_OPTIONS)

// `YYYY-MM-DD` (date-only ISO) without time/offset. `new Date(iso)` would parse
// it as UTC midnight, which rolls back a day in negative-UTC locales — match
// it here so callers don't have to think about the TZ shift.
const DATE_ONLY_ISO = /^\d{4}-\d{2}-\d{2}$/

const toDate = (value: Date | string | number | null | undefined): Date | null => {
  if (value == null) return null
  if (typeof value === 'string' && DATE_ONLY_ISO.test(value)) {
    const [yStr, mStr, dStr] = value.split('-')
    return new Date(Number(yStr), Number(mStr) - 1, Number(dStr))
  }
  const date = value instanceof Date ? value : new Date(value)
  return isNaN(date.getTime()) ? null : date
}

// MM/DD/YYYY — US numeric date format used everywhere in the product.
export const formatUsDate = (value: Date | string | number | null | undefined): string => {
  const date = toDate(value)
  return date ? US_DATE_FORMAT.format(date) : '—'
}

// MM/DD/YYYY, h:mm AM/PM — US numeric date + 12-hour time.
export const formatUsDateTime = (value: Date | string | number | null | undefined): string => {
  const date = toDate(value)
  return date ? US_DATE_TIME_FORMAT.format(date) : '—'
}

// h:mm AM/PM — 12-hour clock, no date.
export const formatUsTime = (value: Date | string | number | null | undefined): string => {
  const date = toDate(value)
  return date ? US_TIME_FORMAT.format(date) : '—'
}

// Accepts either `HH:MM[:SS]` or a full timestamp (`YYYY-MM-DDTHH:MM[:SS]` /
// `YYYY-MM-DD HH:MM[:SS]`). Returns 12-hour wall-clock format like `7:00 AM`.
export const formatScheduledStartTime = (value: string): string => {
  const timePart =
    (value.includes('T')
      ? value.split('T')[1]
      : value.includes(' ')
        ? value.split(' ')[1]
        : value) ?? ''
  const [hStr, mStr] = timePart.split(':')
  const h = Number(hStr)
  const m = Number(mStr)
  const period = h >= 12 ? 'PM' : 'AM'
  const displayHour = h % 12 === 0 ? 12 : h % 12
  return `${displayHour}:${String(m).padStart(2, '0')} ${period}`
}

// Accepts an ISO date (`YYYY-MM-DD`) or full timestamp; formats without
// timezone shifting. Delegates to `formatUsDate`, which detects date-only ISO
// strings and parses them locally instead of through UTC midnight.
export const formatScheduledDate = (iso: string): string => {
  return formatUsDate(iso.length > 10 ? iso : iso.slice(0, 10))
}

// US-format "MM/DD/YYYY, 7:00 AM" output. The wall-clock string is treated
// as-is (no `Z` appended) so it isn't shifted by the runtime's timezone.
export const formatScheduledDateTime = (iso: string | null): string | null => {
  if (!iso) return null
  const date = new Date(iso)
  if (isNaN(date.getTime())) return null
  return US_DATE_TIME_FORMAT.format(date)
}

export const pluralize = (amount: number, word: string, plural?: string) => {
  const suffix = amount === 1 ? '' : (plural ?? 's')
  return `${amount} ${word}${suffix}`
}

// Display form of a dispatch number ("DSP-00042"). Mirrors the backend's
// default numbering config (prefix "DSP-", padded to 5); per-tenant prefix
// overrides only affect server-generated labels, not this display fallback.
export const formatDispatchNumber = (n: number) => `DSP-${String(n).padStart(5, '0')}`

// Sibling display formatters for the other sequence-numbered entities. Same
// "PREFIX-00042" shape as formatDispatchNumber, kept here so the trip / order /
// ticket pages don't each re-declare their own padStart helper.
export const formatTripNumber = (n: number) => `TRP-${String(n).padStart(5, '0')}`
export const formatOrderNumber = (n: number) => `ORD-${String(n).padStart(5, '0')}`
export const formatTicketNumber = (n: number) => `TKT-${String(n).padStart(5, '0')}`

// Render a raw shortfall amount: integers stay bare ("5"), decimals trim
// trailing zeros ("5.5", "5.75"). Truncates rather than rounds — a
// genuinely short 5.999 against a target of 6 must not display as "6"
// and read as complete. Works off the number's canonical string form so
// it also sidesteps the 0.29 * 100 = 28.9999… float drift that a manual
// Math.trunc(n * 100) / 100 would inherit. Assumes `n` is a bounded
// non-negative material quantity or load count — extreme magnitudes
// (|n| ≥ 1e21 or |n| < 1e-6) fall into exponential toString() output
// which this splitter would mishandle, but those are unreachable for
// realistic dispatched quantities.
// Not for currency — use `formatCurrency`.
export const formatShortfallNumber = (n: number): string => {
  const [intPart = '0', decPart = ''] = n.toString().split('.')
  const trimmedDec = decPart.slice(0, 2).replace(/0+$/, '')
  return trimmedDec ? `${intPart}.${trimmedDec}` : intPart
}

// Pluralize a free-text material unit against a count. Word units get an
// English "s" suffix ("ton" → "tons"); short uppercase codes ("T", "lb")
// and any unit containing a non-letter character ("m³", "yd³", "ft²")
// are preserved as-is. Materially safer than `${unit}s` alone since
// `material_unit` is `varchar(20)` free text and can carry either a
// symbolic code or a plain word.
export const pluralizeUnit = (unit: string, count: number): string => {
  if (count === 1) return unit
  const isSymbol = !/^[a-zA-Z]+$/.test(unit)
  const isUppercaseAbbrev = unit.length <= 3 && unit === unit.toUpperCase()
  if (isSymbol || isUppercaseAbbrev) return unit
  return `${unit}s`
}

// "5.5 / 10 tons" for a qty shortfall with a known material unit,
// "5 / 10" (bare) for a qty shortfall with unknown/mixed unit, and
// "2 / 5 loads" for a loads shortfall. The caller supplies `loadsLabel`
// (already pluralized/localized) so the UI language governs the noun;
// material units come from the DB and are left as-is.
export const formatShortfallFraction = (
  shortfall:
    | { kind: 'qty' | 'loads'; delivered: number; target: number; unit: string | null }
    | null
    | undefined,
  options?: { loadsLabel?: string },
): string | null => {
  if (!shortfall || shortfall.target <= 0) return null
  const delivered = formatShortfallNumber(shortfall.delivered)
  const target = formatShortfallNumber(shortfall.target)
  const fraction = `${delivered} / ${target}`
  if (shortfall.kind === 'loads') {
    const label = options?.loadsLabel ?? (shortfall.target === 1 ? 'load' : 'loads')
    return `${fraction} ${label}`
  }
  if (!shortfall.unit) return fraction
  return `${fraction} ${pluralizeUnit(shortfall.unit, shortfall.target)}`
}

// ---------------------------------------------------------------------------
// Value formatters for display. All handle null/undefined/invalid input
// gracefully, returning "—" for blanks so call sites don't have to guard.
// ---------------------------------------------------------------------------

const DEFAULT_LOCALE = 'en-US'

/** Guard: returns true if value is null, undefined, or NaN */
const isBlank = (value: unknown): boolean =>
  value == null || (typeof value === 'number' && Number.isNaN(value))

/**
 * Format a number with locale-appropriate grouping (e.g., 1,234,567).
 * Returns the raw value as a string if input is null, undefined, or non-numeric.
 */
export const formatNumber = (
  value: unknown,
  options?: {
    locale?: string
    decimals?: number
    notation?: 'standard' | 'compact' | 'scientific'
  },
): string => {
  if (isBlank(value)) return '—'
  const num = Number(value)
  if (Number.isNaN(num)) return String(value)

  return new Intl.NumberFormat(options?.locale ?? DEFAULT_LOCALE, {
    notation: options?.notation ?? 'standard',
    minimumFractionDigits: options?.decimals,
    maximumFractionDigits: options?.decimals,
  }).format(num)
}

const METERS_PER_MILE = 1609.344

/**
 * A driving distance in miles, the number only: the unit is the caller's
 * translated text. One decimal under ten miles, where a tenth is still news at
 * a site's gate, and whole miles beyond.
 */
export const formatMiles = (meters: number, options?: { locale?: string }): string => {
  const miles = meters / METERS_PER_MILE
  return formatNumber(miles, { locale: options?.locale, decimals: miles < 10 ? 1 : 0 })
}

/**
 * Format a number as currency.
 * Returns the raw value as a string if input is null, undefined, or non-numeric.
 */
export const formatCurrency = (
  value: unknown,
  options?: { locale?: string; currency?: string; decimals?: number },
): string => {
  if (isBlank(value)) return '—'
  const num = Number(value)
  if (Number.isNaN(num)) return String(value)

  const decimals = options?.decimals ?? 2
  // Intl keeps the sign of an amount that rounds to zero ("-$0.00"), which
  // reads as a loss on a break-even figure.
  const roundsToZero = Math.round(Math.abs(num) * 10 ** decimals) === 0

  return new Intl.NumberFormat(options?.locale ?? DEFAULT_LOCALE, {
    style: 'currency',
    currency: options?.currency ?? 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(roundsToZero ? 0 : num)
}

/**
 * Format a low–high currency span as "$4,000–$5,500", collapsing to a single
 * amount when both ends match. Whole dollars by default: a range is a summary.
 */
export const formatCurrencyRange = (
  range: { min: number; max: number },
  options?: { locale?: string; currency?: string; decimals?: number },
): string => {
  const format = (value: number) =>
    formatCurrency(value, { ...options, decimals: options?.decimals ?? 0 })
  return range.min === range.max ? format(range.min) : `${format(range.min)}–${format(range.max)}`
}

/**
 * Format a quantity: grouped, up to `maxDecimals` places, no trailing-zero
 * padding — "1,250", "24.5", "24.75".
 *
 * Distinct from `formatNumber({ decimals })`, which pins the minimum and the
 * maximum to the same value and so renders a whole number as "24.00". A tonnage
 * or a load count reads better bare.
 */
export const formatQuantity = (
  value: unknown,
  options?: { locale?: string; maxDecimals?: number },
): string => {
  if (isBlank(value)) return '—'
  const num = Number(value)
  if (Number.isNaN(num)) return String(value)

  return new Intl.NumberFormat(options?.locale ?? DEFAULT_LOCALE, {
    maximumFractionDigits: options?.maxDecimals ?? 2,
  }).format(num)
}

/**
 * Normalize an API decimal string to seed an editable input.
 *
 * The pricing columns behind the ticket verifier are numeric(_,4), so Postgres
 * pads them to their full scale and "10" comes back as "10.0000" — four digits
 * of noise on a field the reviewer reads as dollars (PET-732). Unparseable input
 * is passed through untouched rather than blanked, so a bad value stays visible
 * and editable instead of silently vanishing from the form.
 *
 * Rounds through `Intl` rather than `toFixed`: the latter rounds the underlying
 * double, so the half-way cases a money field actually hits go the wrong way —
 * `(1.005).toFixed(2)` is "1.00" where `Intl` gives "1.01". Grouping is off
 * because the result is fed back into an input and has to stay parseable.
 */
export const formatDecimalInputValue = (
  value: string | number | null | undefined,
  decimals = 2,
): string => {
  if (value == null || value === '') return ''
  const num = Number(value)
  if (Number.isNaN(num)) return String(value)

  return new Intl.NumberFormat(DEFAULT_LOCALE, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: false,
  }).format(num)
}

/**
 * "$85.00 /TON" — the rate-badge format shared by the dispatch boards
 * (office order card and carrier job card). Returns null (not "—") on a
 * missing/invalid rate so callers can omit the badge entirely.
 */
export const formatRate = (rate: string | null, unit: string | null): string | null => {
  if (!rate) return null
  const num = parseFloat(rate)
  if (Number.isNaN(num)) return null
  return `${formatCurrency(num)} /${unit?.toUpperCase() ?? '?'}`
}

/**
 * Format a number as a percentage.
 * Expects a decimal (0.125 → "12.5%"), not an integer (12.5).
 * Returns the raw value as a string if input is null, undefined, or non-numeric.
 */
export const formatPercent = (
  value: unknown,
  options?: { locale?: string; decimals?: number },
): string => {
  if (isBlank(value)) return '—'
  const num = Number(value)
  if (Number.isNaN(num)) return String(value)

  return new Intl.NumberFormat(options?.locale ?? DEFAULT_LOCALE, {
    style: 'percent',
    minimumFractionDigits: options?.decimals ?? 1,
    maximumFractionDigits: options?.decimals ?? 1,
  }).format(num)
}

/**
 * Format a contractor's display name as "First Last · Company", or
 * "First Last" when company is missing.
 *
 * Prefers the linked company entity (`companyName`) over the legacy free-text
 * `company` field, which is empty for contractors associated to a company.
 */
export const formatContractorName = (contractor: {
  firstName: string
  lastName: string
  company?: string | null
  companyName?: string | null
}): string => {
  const fullName = `${contractor.firstName} ${contractor.lastName}`
  const company = contractor.companyName ?? contractor.company
  return company ? `${fullName} · ${company}` : fullName
}

/**
 * Format a date value in US numeric format (MM/DD/YYYY).
 * Accepts Date objects, ISO strings, or timestamps.
 * Returns the raw value as a string if input is null, undefined, or invalid.
 */
export const formatDate = (
  value: unknown,
  options?: {
    locale?: string
    variant?: 'short' | 'medium' | 'long' | 'relative' | 'spelled' | 'weekday' | 'monthDay'
    // Without this the runtime's zone decides the calendar day. That is fine
    // in a browser (it is the reader's own zone) but wrong server-side, where
    // the container is UTC: a deadline instant late in the evening renders as
    // the following day. Pass the zone the date's semantics belong to.
    timeZone?: string
  },
): string => {
  if (isBlank(value)) return '—'

  const date = value instanceof Date ? value : new Date(value as string | number)

  if (Number.isNaN(date.getTime())) return String(value)

  if (options?.variant === 'relative') {
    return formatRelativeTime(date)
  }

  // `short` and `medium` are intentionally identical for now — keeping the keys
  // lets us diverge later without rippling through call sites.
  const presets: Record<string, Intl.DateTimeFormatOptions> = {
    short: { month: '2-digit', day: '2-digit', year: 'numeric' },
    medium: { month: '2-digit', day: '2-digit', year: 'numeric' },
    long: {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    },
    // Names the month so the date can't be misread across locales — the
    // numeric presets flip to day-first under `es-*`, which turns a deadline
    // like 09/02 into 02/09 for readers who still expect US ordering.
    spelled: { month: 'long', day: 'numeric', year: 'numeric' },
    // A day this week, as a screen headed "Today" names it: "Monday, Oct 19".
    weekday: { weekday: 'long', month: 'short', day: 'numeric' },
    // A recent day in a line of copy: "Oct 19".
    monthDay: { month: 'short', day: 'numeric' },
  }

  const preset = presets[options?.variant ?? 'medium']
  const formatIn = (timeZone?: string) =>
    new Intl.DateTimeFormat(options?.locale ?? DEFAULT_LOCALE, {
      ...preset,
      ...(timeZone ? { timeZone } : {}),
    }).format(date)

  if (!options?.timeZone) return formatIn()

  // `Intl` throws on an unrecognised IANA name, and one caller is an email
  // template rendered from `tenants.timezone` — an unvalidated varchar. Letting
  // that throw would drop the whole message instead of misdating it, so fail
  // open to the runtime zone the same way endOfDayInZone tolerates a bad zone.
  try {
    return formatIn(options.timeZone)
  } catch (err) {
    if (err instanceof RangeError) return formatIn()
    throw err
  }
}

/**
 * Format a bare calendar day (a Postgres `date`, serialized as `YYYY-MM-DD`).
 */
// `new Date('2026-10-15')` is UTC midnight per spec, so formatting it in the
// runtime zone shows the previous day anywhere west of UTC. Formatting in UTC
// keeps the day the string names.
export const formatCalendarDate = (
  value: string | null | undefined,
  options?: { locale?: string; variant?: 'short' | 'medium' | 'spelled' | 'weekday' },
): string => formatDate(value, { ...options, timeZone: 'UTC' })

/**
 * Format a date as a relative time string (e.g., "3 min ago", "2 days ago").
 *
 * NOTE: This produces a **snapshot** — the string is computed once and does
 * NOT live-update. If you need a ticking relative time, call this inside
 * a useEffect with an interval, or use a dedicated hook.
 *
 * Accepts Date objects, ISO strings, or timestamps.
 * Returns the raw value as a string if input is null, undefined, or invalid.
 */
export const formatRelativeTime = (value: unknown, options?: { locale?: string }): string => {
  if (isBlank(value)) return '—'

  const date = value instanceof Date ? value : new Date(value as string | number)

  if (Number.isNaN(date.getTime())) return String(value)

  const now = Date.now()
  const diffMs = now - date.getTime()
  const absDiff = Math.abs(diffMs)
  const sign = diffMs >= 0 ? -1 : 1 // Intl.RelativeTimeFormat uses negative for past

  const rtf = new Intl.RelativeTimeFormat(options?.locale ?? DEFAULT_LOCALE, {
    numeric: 'auto',
  })

  if (absDiff < 60_000) return rtf.format(sign * Math.round(absDiff / 1000), 'second')
  if (absDiff < 3_600_000) return rtf.format(sign * Math.round(absDiff / 60_000), 'minute')
  if (absDiff < 86_400_000) return rtf.format(sign * Math.round(absDiff / 3_600_000), 'hour')
  if (absDiff < 2_592_000_000) return rtf.format(sign * Math.round(absDiff / 86_400_000), 'day')
  if (absDiff < 31_536_000_000)
    return rtf.format(sign * Math.round(absDiff / 2_592_000_000), 'month')
  return rtf.format(sign * Math.round(absDiff / 31_536_000_000), 'year')
}

export const formatPhoneNumber = (phone: string) => {
  const cleaned = phone.replace(/\D/g, '')
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`
  }
}

/**
 * A tenant slug from a company name.
 *
 * `fallback` is what a name with no ASCII alphanumerics collapses to — a purely
 * non-Latin name, or punctuation. It defaults to `'carrier'` because that was
 * the only caller when this was written, and a contractor tenant reachable at
 * `/carrier/quotes` announces the wrong tenant type in every URL its people
 * see. Pass the one that matches what you are minting.
 */
export const slugifyCompanyName = (name: string, fallback = 'carrier'): string => {
  return (
    name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/[\s-]+/g, '-')
      .replace(/^-|-$/g, '') || fallback
  )
}

/**
 * A short elapsed time: "850ms", "4.3s", "2m 05s", "1h 02m". A missing,
 * negative or non-finite value is shown as '—'.
 */
export const formatDurationMs = (ms: number | null | undefined): string => {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return '—'
  if (ms < 999.5) return `${Math.round(ms)}ms`
  // Below this, one decimal would round up to "60.0s".
  if (ms < 59_950) return `${(ms / 1000).toFixed(1)}s`
  const totalSeconds = Math.round(ms / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`
}

/**
 * An email shown back to the person who typed it, without spelling it out
 * (`AUTH-36`): "s•••@pavingco.com". The domain stays, so a typo there shows;
 * an input with no `@` comes back unchanged.
 */
export const maskEmail = (email: string): string => {
  const at = email.lastIndexOf('@')
  if (at < 1) return email
  return `${email[0]}•••${email.slice(at)}`
}

/**
 * A position in a video, as a player shows it: "0:07", "1:05", "1:02:09".
 * Negative or non-finite input is shown as '—'.
 */
export const formatTimecode = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) return '—'
  const total = Math.floor(seconds)
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const secs = String(total % 60).padStart(2, '0')
  if (hours > 0) return `${hours}:${String(minutes).padStart(2, '0')}:${secs}`
  return `${minutes}:${secs}`
}
